/**
 * "Onderwater venster" — toont de code-laag van het actieve diagram in
 * één van drie modi:
 * - **PlantUML** (default, bidirectioneel) — backed by CodeMirror 6 met
 *   parser + emitter (zie `applyParseResult.ts`).
 * - **Mermaid** (placeholder, bidirectioneel komt in volgende M2-iteratie).
 * - **SVG** (read-only) — gegenereerd uit de active-diagram view-state via
 *   `svgEmitter.ts`. Niet bewerkbaar; copy-knop voor extern gebruik.
 *
 * Bidirectional-sync (PlantUML):
 * - Store → editor: subscribe to revision, re-emit text, dispatch into the
 *   editor as a transaction tagged with `userEvent: "ae.sync"` so we can
 *   tell store-driven updates apart from human edits.
 * - Editor → store: when a transaction's userEvent does NOT start with
 *   "ae.", schedule a debounced parse (~500ms after the last keystroke).
 *   On flush, parse the doc, diff against the live store, and apply via
 *   `applyParseResult`. Errors surface in the pane's header.
 */
import {
  aliasToConceptId,
  emitArchiMateMermaid,
  emitArchiMatePlantUML,
  emitC4PlantUML,
  type ParseError,
  parseArchiMateMermaid,
  parseArchiMatePlantUML,
} from "@ae/domain";
import { openSearchPanel, search, searchKeymap } from "@codemirror/search";
import {
  Annotation,
  EditorState,
  RangeSetBuilder,
  StateEffect,
  StateField,
} from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView, keymap } from "@codemirror/view";
import { basicSetup } from "codemirror";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../../state/session";
import { type RevealMode, useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { applyParseResult } from "./applyParseResult";
import { plantumlAliasHoverTooltip, plantumlAutocomplete } from "./autocomplete";
import { PreviewPane } from "./PreviewPane";
import { findNameSubstitutions } from "./substituteNames";

// SVG-mode is verwijderd 2026-05-24. emitDiagramSvg blijft beschikbaar
// in svgEmitter.ts voor de PNG/SVG-export-flow (DiagramTabs) maar wordt
// niet meer als reveal-mode getoond.

// Tag every dispatch we initiate from the store-sync effect so the
// updateListener can ignore it. Anything else → user edit.
const SyncSource = Annotation.define<"ae.sync">();

const PARSE_DEBOUNCE_MS = 500;

// ---- Selected-relation line-highlight ------------------------------------
//
// CodeMirror state field that paints a soft yellow background on the line(s)
// containing both source-alias and target-alias of the selected relation.
// The TextPane dispatches a `setHighlightSearch` effect whenever the
// canvas-selected relation changes; the field walks the doc, finds the
// matching line, and emits a line-decoration. Empty / null effect = no
// highlight.

/**
 * `mode: "all"` — highlight only lines that contain EVERY alias in the
 * list (relation: source AND target).
 * `mode: "any"` — highlight lines that contain at least one alias
 * (concept-selection: definition line + every relation that mentions
 * it).
 * `mode: "matchers"` — multi-item selectie: line matcht wanneer ÉÉN van
 * de geboden matchers slaagt. Elke matcher is zelf een mini-spec met
 * eigen mode+aliases. Voor multi-instance-multi-select (2026-05-26):
 *   - per concept: 1 matcher met `all` + ["Business_Object(",
 *     alias-of-concept] zodat alleen de definitie-regel oplicht en niet
 *     elke relatie-regel die de concept-alias toevallig bevat.
 *   - per relatie: 1 matcher met `all` + [alias-source, alias-target]
 *     zodat exact die ene relatie-regel oplicht.
 */
interface HighlightSearch {
  mode: "all" | "any" | "matchers";
  aliases?: string[];
  matchers?: Array<{ mode: "all" | "any"; aliases: string[] }>;
}

const setHighlightSearch = StateEffect.define<HighlightSearch | null>();

const lineHighlightDecoration = Decoration.line({
  attributes: { style: "background-color: rgba(250, 204, 21, 0.25);" },
});

function lineMatchesMatcher(
  line: string,
  matcher: { mode: "all" | "any"; aliases: string[] },
): boolean {
  if (matcher.aliases.length === 0) return false;
  return matcher.mode === "all"
    ? matcher.aliases.every((a) => line.includes(a))
    : matcher.aliases.some((a) => line.includes(a));
}

function buildHighlightDecorations(
  doc: { length: number; toString(): string; line(n: number): { from: number; number: number } },
  search: HighlightSearch | null,
): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  if (!search) return builder.finish();
  // Normaliseer naar matchers-array.
  const matchers: Array<{ mode: "all" | "any"; aliases: string[] }> =
    search.mode === "matchers"
      ? (search.matchers ?? [])
      : [{ mode: search.mode, aliases: search.aliases ?? [] }];
  if (matchers.length === 0) return builder.finish();
  const text = doc.toString();
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const matches = matchers.some((m) => lineMatchesMatcher(line, m));
    if (matches) {
      const lineInfo = doc.line(i + 1);
      builder.add(lineInfo.from, lineInfo.from, lineHighlightDecoration);
    }
  }
  return builder.finish();
}

const highlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(value, tr) {
    let next = value.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(setHighlightSearch)) {
        next = buildHighlightDecorations(tr.state.doc, e.value);
      }
    }
    // If the doc changed (user edits) without a fresh search effect,
    // we keep the mapped decorations — they still anchor to the same
    // text positions.
    return next;
  },
  provide: (f) => EditorView.decorations.from(f),
});

export function TextPane() {
  const { i18n, t } = useTranslation();
  const open = useUi((s) => s.textPaneOpen);
  const setOpen = useUi((s) => s.setTextPaneOpen);
  const pinned = useUi((s) => s.textPanePinned);
  const setPinned = useUi((s) => s.setTextPanePinned);
  const mode = useUi((s) => s.revealMode);
  const setMode = useUi((s) => s.setRevealMode);
  const previewOpen = useUi((s) => s.revealPreviewOpen);
  const setPreviewOpen = useUi((s) => s.setRevealPreviewOpen);
  const plantumlLineType = useUi((s) => s.plantumlLineType);
  const plantumlPerInstance = useUi((s) => s.plantumlPerInstance);
  const setPlantumlPerInstance = useUi((s) => s.setPlantumlPerInstance);
  const revision = useWorkspaceData((s) => s.revision);
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const [errors, setErrors] = useState<ParseError[]>([]);

  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const selectedRelationId = useUi((s) => s.selectedRelationId);
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  // Multi-instance-refactor (2026-05-26): multi-selectie op canvas
  // (rubber-band, Cmd+klik) → meerdere aliases tegelijk highlight.
  const selectedConceptIds = useUi((s) => s.selectedConceptIds);
  const selectedRelationIds = useUi((s) => s.selectedRelationIds);
  // Live reference to whichever CodeMirror editor (PlantUML / Mermaid)
  // is currently mounted. Used by the toolbar 🔍 button to open the
  // search panel programmatically. Cleared when the editor unmounts so
  // we never call into a stale view.
  const activeEditorViewRef = useRef<EditorView | null>(null);

  // Clear stale parse-errors when the user switches diagrams or modes —
  // the errors banner is per-document, not per-session.
  useEffect(() => {
    setErrors([]);
  }, [activeDiagramId, mode]);

  // Filter concepts + relations to those that are actually on the
  // active diagram. The Onderwater venster shows the code-laag van het
  // CURRENT diagram, niet de hele workspace.
  //
  // Veilig nu omdat `applyParseResult` een scope-param krijgt: bij
  // de verwijder-pass kijkt 't alleen naar concepten/relaties in deze
  // scope, en haalt ze alleen van DIT diagram (niet uit het model).
  // Off-diagram items in andere diagrammen blijven onaangeroerd.
  const visibleOnActiveDiagram = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    if (!activeDiagramId) {
      return {
        concepts: [],
        relations: [],
        conceptIdSet: new Set<string>(),
        relationIdSet: new Set<string>(),
      };
    }
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) {
      return {
        concepts: [],
        relations: [],
        conceptIdSet: new Set<string>(),
        relationIdSet: new Set<string>(),
      };
    }
    const conceptIdSet = new Set<string>();
    for (const [key, ve] of Object.entries(diagram.viewState)) {
      if (ve.position === undefined) continue;
      if (ve.hidden === true) continue;
      conceptIdSet.add(ve.ref ?? key);
    }
    const concepts = store
      .listConcepts()
      .filter((c) => !c.tombstoneInBranch && conceptIdSet.has(c.id));
    // Set van concept-id's die ECHT bestaan op dit diagram. Niet
    // gelijk aan `conceptIdSet` — die laatste heeft viewState-keys
    // (waaronder tombstoned/hard-deleted concepten waarvan de
    // viewState-entry nog niet is opgeruimd). Voor relatie-filtering
    // gebruiken we de live set zodat orphan-relaties (verwijzend
    // naar een verwijderd concept) niet door de filter glippen en
    // als ghost-knoopje in PlantUML-render eindigen.
    const liveConceptIds = new Set(concepts.map((c) => c.id));
    // Per-diagram-scoping (zelfde regel als Canvas.tsx): relatie is hier
    // alleen zichtbaar als `viewState[r.id]` bestaat op dit diagram, of
    // als de relatie legacy is (nog nergens een viewState-entry).
    const placedRelationIds = new Set<string>();
    const allRelations = store.listRelations();
    for (const d of store.listDiagrams()) {
      for (const r of allRelations) {
        if (d.viewState[r.id] !== undefined) placedRelationIds.add(r.id);
      }
    }
    const relations = allRelations.filter((r) => {
      if (r.tombstoneInBranch) return false;
      if (!liveConceptIds.has(r.sourceConceptId)) return false;
      if (!liveConceptIds.has(r.targetConceptId)) return false;
      const ve = diagram.viewState[r.id];
      if (ve?.hidden === true) return false;
      const isLegacy = !placedRelationIds.has(r.id);
      const onThisDiagram = ve !== undefined;
      if (!isLegacy && !onThisDiagram) return false;
      return true;
    });
    const relationIdSet = new Set(relations.map((r) => r.id));
    return { concepts, relations, conceptIdSet, relationIdSet };
  }, [revision, activeDiagramId]);

  const plantumlText = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const handles: Record<string, { source?: string; target?: string }> = {};
    const waypoints: Record<string, ReadonlyArray<{ x: number; y: number }>> = {};
    const containment: Record<string, string> = {};
    let title: string | undefined;
    if (activeDiagramId) {
      const diagram = store.getDiagram(activeDiagramId);
      if (diagram) {
        for (const r of visibleOnActiveDiagram.relations) {
          const ve = diagram.viewState[r.id];
          if (!ve) continue;
          const entry: { source?: string; target?: string } = {};
          if (ve.sourceHandle) entry.source = ve.sourceHandle;
          if (ve.targetHandle) entry.target = ve.targetHandle;
          if (entry.source || entry.target) handles[r.id] = entry;
          // Canvas-waypoints → emitter genereert hidden-rectangle-chain
          // zodat ELK de knikken visueel respecteert in de PlantUML-render.
          if (ve.waypoints && ve.waypoints.length > 0) {
            waypoints[r.id] = ve.waypoints;
          }
        }
        // Visual containment from the canvas → PlantUML brace-blocks.
        // Combineert expliciete `containerId` (drag-naar-binnen) met
        // geometrische overlap (concept-middelpunt valt binnen een
        // Grouping-box). Bij overlap met meerdere Groupings wint de
        // kleinste — most-specific.
        for (const c of visibleOnActiveDiagram.concepts) {
          const ve = diagram.viewState[c.id];
          if (!ve?.containerId) continue;
          const parentVe = diagram.viewState[ve.containerId];
          const parentConceptId = parentVe?.ref ?? ve.containerId;
          if (parentConceptId === c.id) continue;
          containment[c.id] = parentConceptId;
        }
        // Geometrische detectie — voor C4-stijl-nesting (Software System
        // bevat Container, Container bevat Component) én voor Groupings.
        // Iedere concept-box telt als potentiële container; "parent moet
        // minstens 1.5× zo groot zijn als het kind" voorkomt dat twee
        // ongeveer-even-grote concepten naast elkaar elkaar als parent
        // zien wanneer hun bounding-boxes net overlappen. Drawing-types
        // doen niet mee als parent omdat ze als comment worden geëmit.
        const containerBoxes = visibleOnActiveDiagram.concepts
          .filter((g) => !g.typeId.startsWith("drawing."))
          .map((g) => {
            const ve = diagram.viewState[g.id];
            if (!ve?.position) return null;
            const w = ve.width ?? 140;
            const h = ve.height ?? 60;
            return { id: g.id, x: ve.position.x, y: ve.position.y, w, h, area: w * h };
          })
          .filter((b): b is NonNullable<typeof b> => b !== null);
        for (const c of visibleOnActiveDiagram.concepts) {
          if (containment[c.id]) continue;
          if (c.typeId.startsWith("drawing.")) continue;
          const ve = diagram.viewState[c.id];
          if (!ve?.position) continue;
          const cw = ve.width ?? 140;
          const ch = ve.height ?? 60;
          const childArea = cw * ch;
          const cx = ve.position.x + cw / 2;
          const cy = ve.position.y + ch / 2;
          let best: { id: string; area: number } | null = null;
          for (const g of containerBoxes) {
            if (g.id === c.id) continue;
            if (g.area < childArea * 1.5) continue; // parent ≥ 1.5× kind
            if (cx >= g.x && cx <= g.x + g.w && cy >= g.y && cy <= g.y + g.h) {
              if (!best || g.area < best.area) best = { id: g.id, area: g.area };
            }
          }
          if (best) containment[c.id] = best.id;
        }
        // Diagram description → PlantUML `title` statement. Falls
        // back to the bilingual `description` field; only emitted
        // when non-empty after `resolveBilingual` lookup.
        const desc = diagram.description
          ? (diagram.description[lang] ?? diagram.description.nl ?? diagram.description.en ?? "")
          : "";
        if (desc.trim().length > 0) title = desc.trim();
      }
    }
    // View-kind-router: c4.* → C4-emitter; sharp.* → ArchiMate-emitter
    // met stereotype-comment-trailer (geen Sharp-eigen library). Andere
    // view-kinds → ArchiMate-emitter zonder Sharp-trailer. Houdt
    // TextPane's emit-pad in sync met `revealText.ts` voor de
    // GridPane-preview.
    const activeViewKind = activeDiagramId
      ? store.getDiagram(activeDiagramId)?.viewKind
      : undefined;
    if (activeViewKind?.startsWith("c4.")) {
      // C4-emitter gebruikt alleen source-handle (geen target-suffix in
      // stdlib). Strip dus naar `{source}`-only om mee te sturen.
      const c4Handles: Record<string, { source?: string }> = {};
      for (const [relId, h] of Object.entries(handles)) {
        if (h.source) c4Handles[relId] = { source: h.source };
      }
      return emitC4PlantUML(visibleOnActiveDiagram.concepts, visibleOnActiveDiagram.relations, {
        lang,
        viewKind: activeViewKind,
        lineType: plantumlLineType,
        ...(Object.keys(c4Handles).length > 0 ? { handles: c4Handles } : {}),
        ...(title ? { title } : {}),
      });
    }
    const isSharpView = activeViewKind?.startsWith("sharp.") ?? false;
    // Per-instance modus: bouw instancesByConcept + relationOccurrences.
    let instancesByConcept: Record<string, string[]> | undefined;
    let relationOccurrences:
      | Array<{ relationId: string; sourceEntryKey?: string; targetEntryKey?: string }>
      | undefined;
    if (plantumlPerInstance && activeDiagramId) {
      const diagram = useWorkspaceData.getState().store.getDiagram(activeDiagramId);
      if (diagram) {
        instancesByConcept = {};
        relationOccurrences = [];
        for (const [k, ve] of Object.entries(diagram.viewState)) {
          if (ve.hidden === true) continue;
          if (ve.position !== undefined || ve.type === "object" || ve.slotId !== undefined) {
            const cid = ve.ref ?? k;
            if (!instancesByConcept[cid]) instancesByConcept[cid] = [];
            instancesByConcept[cid].push(k);
            continue;
          }
          if (ve.type === "relation" || (ve.position === undefined && ve.ref !== undefined)) {
            const rid = ve.ref ?? k;
            relationOccurrences.push({
              relationId: rid,
              ...(ve.sourceInstanceId ? { sourceEntryKey: ve.sourceInstanceId } : {}),
              ...(ve.targetInstanceId ? { targetEntryKey: ve.targetInstanceId } : {}),
            });
          }
        }
      }
    }
    return emitArchiMatePlantUML(
      visibleOnActiveDiagram.concepts,
      visibleOnActiveDiagram.relations,
      {
        lang,
        handles,
        lineType: plantumlLineType,
        ...(Object.keys(waypoints).length > 0 ? { waypoints } : {}),
        ...(Object.keys(containment).length > 0 ? { containment } : {}),
        ...(title ? { title } : {}),
        ...(isSharpView ? { includeStereotypeComments: true } : {}),
        ...(instancesByConcept ? { instancesByConcept } : {}),
        ...(relationOccurrences ? { relationOccurrences } : {}),
      },
    );
  }, [
    revision,
    lang,
    activeDiagramId,
    visibleOnActiveDiagram,
    plantumlLineType,
    plantumlPerInstance,
  ]);

  const mermaidText = useMemo(() => {
    if (mode !== "mermaid") return "";
    void revision;
    return emitArchiMateMermaid(visibleOnActiveDiagram.concepts, visibleOnActiveDiagram.relations, {
      lang,
    });
  }, [mode, revision, lang, visibleOnActiveDiagram]);

  // When something is selected on the canvas, pre-compute a search
  // pattern that the editor can use to highlight the matching line(s).
  //
  //  - **Relation selected** → match lines that contain BOTH the source
  //    and target aliases (the relation-line in PlantUML / Mermaid).
  //  - **Concept selected** → match lines that contain the concept's
  //    own alias. Hits both the concept-definition line AND any
  //    relation lines that mention the concept; together they make
  //    the concept's "footprint" in the text easy to spot.
  //
  // Relation takes precedence when both happen to be set.
  const highlightSearch = useMemo<HighlightSearch | null>(() => {
    void revision;
    const aliasOf = (id: string) => `n_${id.replace(/-/g, "_")}`;
    const store = useWorkspaceData.getState().store;
    // Multi-instance-refactor (2026-05-26): bouw één matcher per
    // geselecteerd item. Zo lichten ALLEEN de regels op die exact bij
    // dat item horen — geen "any-alias"-match die ook andere relaties
    // mee-highlight'd alleen omdat ze toevallig dezelfde concept-alias
    // bevatten. Consistent met Tabel-modus (matcht per-ID exact).
    //
    // Per concept: matcher met "Business_Object(" + alias → alleen de
    // definitie-regel.
    // Per relatie: matcher met source-alias + target-alias (beide) →
    // alleen die ene Rel_-regel.
    const conceptIds = new Set<string>(selectedConceptIds);
    if (selectedConceptId) conceptIds.add(selectedConceptId);
    const relationIds = new Set<string>(selectedRelationIds);
    if (selectedRelationId) relationIds.add(selectedRelationId);
    const matchers: Array<{ mode: "all" | "any"; aliases: string[] }> = [];
    for (const cid of conceptIds) {
      // Concept-definitie-regels hebben formaat
      // `<TypeProc>(<alias>, "<label>", …)` — alias als eerste arg,
      // gevolgd door een string-literal (de label).
      // Relatie-regels hebben formaat `Rel_<…>(<src>, <tgt>, "<label>")`
      // — eerste arg is óók een alias maar dan gevolgd door komma+alias
      // (NIET door komma+quote).
      // Pattern `(<alias>, "` discrimineert dus correct: matcht alleen
      // concept-definitie-regels, niet Rel_*-regels waarin de alias als
      // source/target voorkomt.
      matchers.push({ mode: "all", aliases: [`(${aliasOf(cid)}, "`] });
    }
    for (const rid of relationIds) {
      const r = store.getRelation(rid);
      if (!r) continue;
      matchers.push({
        mode: "all",
        aliases: [aliasOf(r.sourceConceptId), aliasOf(r.targetConceptId)],
      });
    }
    if (matchers.length === 0) return null;
    return { mode: "matchers", matchers };
  }, [selectedRelationId, selectedConceptId, selectedConceptIds, selectedRelationIds, revision]);

  // Welke tekst hoort bij welke modus — voor de copy-knop in de header.
  const currentText = mode === "plantuml" ? plantumlText : mermaidText;

  if (!open) {
    return (
      <button
        type="button"
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised)"
        onClick={() => setOpen(true)}
        title={t("textPane.openHint")}
      >
        {t("textPane.openButton")}
      </button>
    );
  }

  return (
    <section
      data-scope="text-pane"
      className="flex h-full min-h-0 flex-col border-(--ld-border) border-t bg-(--ld-surface)"
    >
      <header className="flex items-center justify-between border-(--ld-border) border-b bg-(--ld-raised) px-3 py-2">
        <div className="flex items-baseline gap-3">
          <h2 className="font-semibold text-(--ld-text) text-sm">{t("textPane.title")}</h2>
          {mode === "plantuml" && errors.length > 0 && (
            <span className="text-(--ld-notice-ink) text-xs">
              {t("textPane.parseErrors", { count: errors.length })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* ModeSwitcher — alleen zichtbaar wanneer Preview aan staat.
              Per ontwerp 2026-05-23: PlantUML/Mermaid/SVG-keuze bepaalt
              welke render in de preview-pane verschijnt; bij preview-uit
              is keuze irrelevant en wordt 'ie verborgen om header-clutter
              te voorkomen. */}
          {previewOpen && <ModeSwitcher mode={mode} onChange={setMode} />}
          <button
            type="button"
            className={`rounded border border-(--ld-control) px-2 py-1 font-medium text-sm hover:bg-(--ld-raised) ${
              previewOpen
                ? "bg-(--ld-active) text-(--ld-accent)"
                : "bg-(--ld-surface) text-(--ld-text)"
            }`}
            onClick={() => setPreviewOpen(!previewOpen)}
            title={previewOpen ? t("textPane.preview.hideTitle") : t("textPane.preview.showTitle")}
            aria-pressed={previewOpen}
          >
            {previewOpen ? t("textPane.preview.on") : t("textPane.preview.off")}
          </button>
          {/* Per-instance toggle — alleen zichtbaar in PlantUML-modus.
              Default OFF: één Business_Object per concept-id (logical).
              ON: één regel per viewState-entry (multi-instance), met
              alias-suffix `_N` voor 2e+ instance. Niet-Archi-conform;
              roundtrip via parser nog niet ondersteund — preview kan
              kapot gaan op kroki bij complexe scenarios. */}
          {mode === "plantuml" && (
            <button
              type="button"
              className={`rounded border border-(--ld-control) px-2 py-1 font-medium text-sm hover:bg-(--ld-raised) ${
                plantumlPerInstance
                  ? "bg-(--ld-notice-bg) text-(--ld-notice-ink)"
                  : "bg-(--ld-surface) text-(--ld-text)"
              }`}
              onClick={() => setPlantumlPerInstance(!plantumlPerInstance)}
              title={
                plantumlPerInstance
                  ? t("textPane.perInstance.onTitle")
                  : t("textPane.perInstance.offTitle")
              }
              aria-pressed={plantumlPerInstance}
            >
              {plantumlPerInstance ? t("textPane.perInstance.on") : t("textPane.perInstance.off")}
            </button>
          )}
          <CopyButton text={currentText} />
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={() => {
              const view = activeEditorViewRef.current;
              if (view) {
                openSearchPanel(view);
                view.focus();
              }
            }}
            title={t("textPane.search.title")}
            aria-label={t("textPane.search.aria")}
          >
            🔍
          </button>
          <button
            type="button"
            className={`rounded px-1 text-base leading-none ${
              pinned
                ? "text-(--ld-accent) hover:text-(--ld-accent)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setPinned(!pinned)}
            title={pinned ? t("textPane.pin.pinnedTitle") : t("textPane.pin.unpinnedTitle")}
            aria-label={pinned ? t("diagramsPanel.pin.unpinAria") : t("diagramsPanel.pin.pinAria")}
            aria-pressed={pinned}
          >
            {pinned ? "📌" : "📍"}
          </button>
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={() => setOpen(false)}
            title={t("textPane.hideHint")}
          >
            {t("textPane.hideButton")}
          </button>
        </div>
      </header>
      {mode === "plantuml" && errors.length > 0 && (
        <ul className="border-(--ld-border) border-b bg-(--ld-notice-bg) px-3 py-1.5 text-(--ld-notice-ink) text-xs">
          {errors.slice(0, 5).map((e) => (
            <li key={`${e.line}-${e.message}`} className="font-mono">
              {t("textPane.errorLine", { line: e.line, message: e.message })}
            </li>
          ))}
        </ul>
      )}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/*
          `key={activeDiagramId}` force-remounts the CodeMirror editor
          whenever the active diagram changes. Without this, edge cases
          with parse errors or empty editor content would leave the
          editor "stuck" on the previous diagram's text — the bidirectional
          sync compares strings and skips when current === text, which
          can lock the view in a stale state. A fresh mount per diagram
          guarantees a clean slate (and resets the parse-error banner).

          Side-by-side preview lives next to the editor when
          `previewOpen` is true (PlantUML + Mermaid only — SVG mode IS
          the preview). 50/50 split via two `flex-1` children.
        */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {mode === "plantuml" && (
            <CodeMirrorEditor
              key={`plantuml:${activeDiagramId ?? "none"}`}
              text={plantumlText}
              notation="plantuml"
              lang={lang}
              workspaceId={workspaceId}
              branchId={branchId}
              onErrors={setErrors}
              scope={{
                conceptIds: visibleOnActiveDiagram.conceptIdSet,
                relationIds: visibleOnActiveDiagram.relationIdSet,
              }}
              highlightSearch={highlightSearch}
              onView={(v) => {
                activeEditorViewRef.current = v;
              }}
            />
          )}
          {mode === "mermaid" && (
            <CodeMirrorEditor
              key={`mermaid:${activeDiagramId ?? "none"}`}
              text={mermaidText}
              notation="mermaid"
              lang={lang}
              workspaceId={workspaceId}
              branchId={branchId}
              onErrors={setErrors}
              scope={{
                conceptIds: visibleOnActiveDiagram.conceptIdSet,
                relationIds: visibleOnActiveDiagram.relationIdSet,
              }}
              highlightSearch={highlightSearch}
              onView={(v) => {
                activeEditorViewRef.current = v;
              }}
            />
          )}
        </div>
        {previewOpen && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <PreviewPane text={mode === "plantuml" ? plantumlText : mermaidText} notation={mode} />
          </div>
        )}
      </div>
    </section>
  );
}

/** Three-button segmented switcher in the pane header. */
function ModeSwitcher({ mode, onChange }: { mode: RevealMode; onChange: (m: RevealMode) => void }) {
  const opts: Array<{ id: RevealMode; label: string }> = [
    { id: "plantuml", label: "PlantUML" },
    { id: "mermaid", label: "Mermaid" },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded border border-(--ld-control) text-xs">
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`px-2 py-0.5 ${
            mode === o.id
              ? "bg-(--ld-primary) font-semibold text-(--ld-primary-ink)"
              : "bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
          }`}
          onClick={() => onChange(o.id)}
          aria-pressed={mode === o.id}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CodeMirrorEditor({
  text,
  notation,
  lang,
  workspaceId,
  branchId,
  onErrors,
  scope,
  highlightSearch,
  onView,
}: {
  text: string;
  /** Which parser to use on edits — controls the bidirectional-sync path. */
  notation: "plantuml" | "mermaid";
  lang: "nl" | "en";
  workspaceId: string | null;
  branchId: string | null;
  onErrors: (errors: ParseError[]) => void;
  /**
   * The set of concept- and relation-ids the editor's text represents.
   * Passed straight to `applyParseResult` so its removal pass only
   * considers items in this scope (and only removes them from the active
   * diagram, never from the model); off-diagram items remain untouched
   * even when they don't appear in the parsed text.
   */
  scope: { conceptIds: Set<string>; relationIds: Set<string> };
  /**
   * Lines containing the listed aliases get a soft yellow background.
   * `mode: "all"` requires every alias on the line (relation = both
   * endpoints); `mode: "any"` highlights lines with at least one alias
   * (concept-selection footprint). `null` clears the highlight.
   */
  highlightSearch: HighlightSearch | null;
  /**
   * Notifies the parent of the live `EditorView` instance — fires once
   * after mount with the view, and once with `null` on unmount. The
   * parent uses this to drive imperative actions (e.g. opening the
   * search panel from a header button) without having to re-create
   * the editor.
   */
  onView?: (view: EditorView | null) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const textRef = useRef(text);
  textRef.current = text;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Dirty-flag: true zodra de modelleur zelf iets heeft getypt. Blijft
  // staan tot de editor focus verliest (= klikken in canvas / ander
  // paneel) of de editor remount (= diagram-switch via key-prop).
  // Zolang dirty=true skipt de store→editor sync-effect elke dispatch
  // zodat de re-emit van het store-model (deterministisch, mogelijk
  // hergeordend) de doc niet overschrijft.
  const dirtyRef = useRef<boolean>(false);
  // Latest values for the schedule callback — refs avoid recreating the
  // EditorView on every prop change.
  const langRef = useRef(lang);
  langRef.current = lang;
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  const branchIdRef = useRef(branchId);
  branchIdRef.current = branchId;
  const onErrorsRef = useRef(onErrors);
  onErrorsRef.current = onErrors;
  const notationRef = useRef(notation);
  notationRef.current = notation;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  useEffect(() => {
    if (!hostRef.current) return;

    const scheduleParse = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        const view = viewRef.current;
        if (!view) return;
        const ws = workspaceIdRef.current;
        const br = branchIdRef.current;
        if (!ws || !br) return;
        const doc = view.state.doc.toString();
        let parsed: ReturnType<typeof parseArchiMatePlantUML>;
        if (notationRef.current === "plantuml") {
          // PlantUML-only: substitute "Concept name" → n_<uuid> in any
          // Rel_*(...) calls. The user can type relations by name; we
          // rewrite them in place so the parser stays simple and the
          // canonical form survives a save/reload.
          const subs = findNameSubstitutions(doc, useWorkspaceData.getState().store);
          let workingDoc = doc;
          if (subs.length > 0) {
            const ordered = [...subs].sort((a, b) => b.from - a.from);
            for (const s of ordered) {
              workingDoc = workingDoc.slice(0, s.from) + s.insert + workingDoc.slice(s.to);
            }
            view.dispatch({
              changes: subs.map((s) => ({ from: s.from, to: s.to, insert: s.insert })),
              annotations: SyncSource.of("ae.sync"),
            });
          }
          parsed = parseArchiMatePlantUML(workingDoc);
        } else {
          // Mermaid: name-substitution helper is PlantUML-specific (it
          // expects `Rel_*(…)` syntax). Mermaid users type the canonical
          // `n_<uuid>` aliases that the emitter produces; the parser
          // accepts unknown aliases as new-concept-creation.
          parsed = parseArchiMateMermaid(doc);
        }
        onErrorsRef.current(parsed.errors);
        // Even if the parser flagged errors on some lines, still apply the
        // valid concepts/relations — partial recovery is friendlier than
        // refusing to update.
        void applyParseResult(
          useWorkspaceData.getState(),
          ws,
          br,
          parsed,
          langRef.current,
          scopeRef.current,
        );
      }, PARSE_DEBOUNCE_MS);
    };

    const updateListener = EditorView.updateListener.of((u) => {
      if (!u.docChanged) return;
      // Skip our own store-sync transactions to avoid feedback loops.
      const isSync = u.transactions.some((tr) => tr.annotation(SyncSource) === "ae.sync");
      if (isSync) return;
      dirtyRef.current = true;
      scheduleParse();
    });

    // Bidirectionele selectie: cursor → canvas-selectie. Wanneer de
    // modelleur de cursor in 't Onderwater venster verplaatst, kijken we
    // welk concept-id of relatie-id onder de cursor staat en dispatchen
    // die naar de UI-store. Het canvas re-rendert dan de node/edge als
    // 'geselecteerd', én de yellow-line-highlight (canvas → text) blijft
    // automatisch synchroon want die luistert al op dezelfde store-key.
    //
    // Geen feedback-loop: een setHighlightSearch-effect uit canvas → text
    // wijzigt enkel decoraties, geen cursor-positie, dus deze listener
    // firet daar niet door.
    //
    // We firen alleen bij selectionSet (cursor verplaatst zonder typing)
    // óf bij user-event "select.pointer" (klik), niet bij elke keystroke.
    const selectionListener = EditorView.updateListener.of((u) => {
      if (!u.selectionSet) return;
      // Skip onze eigen sync-transacties.
      const isSync = u.transactions.some((tr) => tr.annotation(SyncSource) === "ae.sync");
      if (isSync) return;
      const pos = u.state.selection.main.head;
      const line = u.state.doc.lineAt(pos);
      const lineText = line.text;
      const cursorInLine = pos - line.from;
      // Word-grens rond cursor: alfanum + underscore (= alias-tekens).
      let wordStart = cursorInLine;
      while (wordStart > 0 && /[A-Za-z0-9_]/.test(lineText[wordStart - 1] ?? "")) {
        wordStart--;
      }
      let wordEnd = cursorInLine;
      while (wordEnd < lineText.length && /[A-Za-z0-9_]/.test(lineText[wordEnd] ?? "")) {
        wordEnd++;
      }
      const word = lineText.slice(wordStart, wordEnd);
      const ui = useUi.getState();
      const store = useWorkspaceData.getState().store;
      // ── Case 1: cursor staat ON een `n_<uuid>`-alias ─────────────
      // Concept-selectie heeft voorrang boven relation-selectie zodra
      // de cursor letterlijk op een alias-token staat. Anders zou
      // klikken op de source/target van een Rel_-regel een relatie
      // selecteren waarvan de modelleur juist de endpoint wil zien.
      if (word.startsWith("n_")) {
        const conceptId = aliasToConceptId(word);
        if (conceptId && store.getConcept(conceptId)) {
          if (ui.selectedConceptId !== conceptId) {
            ui.selectConcept(conceptId);
          }
          return;
        }
      }
      // ── Case 2: cursor staat op de Rel_<Word>-procedurenaam ──────
      // Alleen relation-selectie aanslaan wanneer de cursor LETTERLIJK
      // op het `Rel_<Word>`-token staat (geen aliassen, geen labels).
      // Voorkomt dat typen binnen een quoted naam-prefix (autocomplete-
      // context) per ongeluk de relatie selecteert.
      if (word.startsWith("Rel_")) {
        const relMatch =
          /^\s*Rel_[A-Za-z][\w]*\s*\(\s*(n_[A-Za-z0-9_]+)\s*,\s*(n_[A-Za-z0-9_]+)/.exec(lineText);
        if (relMatch) {
          const sourceId = aliasToConceptId(relMatch[1] as string);
          const targetId = aliasToConceptId(relMatch[2] as string);
          if (sourceId && targetId) {
            // PlantUML-emitter swapt Composition / Aggregation
            // (target = whole, source = part), dus zoeken op beide
            // varianten — de typeId weten we niet zeker uit de regel
            // alleen (procedure-alias-mapping is many-to-one).
            const candidates = store.listRelations().filter((r) => {
              if (r.tombstoneInBranch) return false;
              return (
                (r.sourceConceptId === sourceId && r.targetConceptId === targetId) ||
                (r.sourceConceptId === targetId && r.targetConceptId === sourceId)
              );
            });
            const rel = candidates[0];
            if (rel && ui.selectedRelationId !== rel.id) {
              ui.selectRelation(rel.id);
            }
            return;
          }
        }
      }
      // Cursor staat op iets dat geen alias en geen Rel_-token is →
      // selectie niet wissen (modelleur kan even tussendoor naar een
      // comment of lege regel klikken — laat de canvas-selectie staan).
    });

    // Blur = "modelleur is klaar met deze edit-sessie"; clear de dirty-
    // flag zodat een vervolg-sync (bv. na een canvas-edit) wel netjes
    // mag landen wanneer de modelleur terug komt.
    const blurListener = EditorView.domEventHandlers({
      blur: () => {
        dirtyRef.current = false;
        return false;
      },
    });

    // Alleen kijken (leesrecht op het workpackage): typen doet niets. Een
    // filter i.p.v. `EditorState.readOnly`, zodat het meeschakelt zodra het
    // workpackage geladen is; de sync vanuit de store blijft doorkomen.
    const readOnlyFilter = EditorState.transactionFilter.of((tr) =>
      tr.docChanged && tr.annotation(SyncSource) !== "ae.sync" && useSession.getState().readOnly
        ? []
        : tr,
    );

    const state = EditorState.create({
      doc: textRef.current,
      extensions: [
        basicSetup,
        readOnlyFilter,
        updateListener,
        selectionListener,
        blurListener,
        highlightField,
        // Type-aware autocomplete (M2 §2.1): popup met PlantUML-procedure-
        // namen via Ctrl/Cmd+Space + hover-tooltip op n_<uuid>-aliases
        // die de naam van 't onderliggende concept toont. Alleen PlantUML-
        // mode — Mermaid heeft andere syntax en eigen alias-conventies.
        ...(notation === "plantuml"
          ? [plantumlAutocomplete(lang), plantumlAliasHoverTooltip(lang)]
          : []),
        // Cmd/Ctrl+F opens the find-panel; Cmd/Ctrl+G goes to next
        // match. Highlight-while-typing matches every occurrence in
        // the doc with a soft yellow underlay.
        search({ top: true }),
        keymap.of(searchKeymap),
        // Kleuren uit de ledger-tokens, zodat de editor de donkere modus
        // van het OS volgt (de basisstijl van CodeMirror is alleen licht).
        EditorView.theme({
          "&": {
            height: "100%",
            fontSize: "12px",
            backgroundColor: "var(--ld-raised)",
            color: "var(--ld-text)",
          },
          ".cm-content": {
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            caretColor: "var(--ld-text)",
          },
          ".cm-scroller": { overflow: "auto" },
          ".cm-cursor, .cm-dropCursor": { borderLeftColor: "var(--ld-text)" },
          ".cm-gutters": {
            backgroundColor: "var(--ld-surface)",
            color: "var(--ld-muted)",
            borderRight: "1px solid var(--ld-border)",
          },
          ".cm-activeLine": {
            backgroundColor: "color-mix(in srgb, var(--ld-accent) 8%, transparent)",
          },
          ".cm-activeLineGutter": { backgroundColor: "var(--ld-active)", color: "var(--ld-text)" },
          "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
            { backgroundColor: "color-mix(in srgb, var(--ld-accent) 30%, transparent)" },
          ".cm-panels": {
            backgroundColor: "var(--ld-surface)",
            color: "var(--ld-text)",
            borderColor: "var(--ld-border)",
          },
          ".cm-textfield": {
            backgroundColor: "var(--ld-raised)",
            color: "var(--ld-text)",
            border: "1px solid var(--ld-muted)",
          },
          ".cm-button": {
            backgroundImage: "none",
            backgroundColor: "var(--ld-surface)",
            color: "var(--ld-text)",
            border: "1px solid var(--ld-control)",
          },
          ".cm-tooltip": {
            backgroundColor: "var(--ld-surface)",
            color: "var(--ld-text)",
            border: "1px solid var(--ld-control)",
          },
          ".cm-tooltip-autocomplete > ul > li[aria-selected]": {
            backgroundColor: "var(--ld-active)",
            color: "var(--ld-text)",
          },
          ".cm-matchingBracket": {
            backgroundColor: "color-mix(in srgb, var(--ld-positive) 25%, transparent)",
          },
        }),
      ],
    });
    const view = new EditorView({ state, parent: hostRef.current });
    viewRef.current = view;
    onView?.(view);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      view.destroy();
      viewRef.current = null;
      onView?.(null);
    };
    // `onView` callback is intentionally not a dep — it's a stable
    // bridge that should fire only on mount/unmount, not whenever the
    // parent passes a new function reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store → editor sync. Tag with SyncSource so de updateListener 'm
  // negeert. Skip wanneer de editor `dirty` is (modelleur heeft getypt
  // sinds de laatste focus-acquisition): re-emit van de store na een
  // parse-cycle is deterministisch en mogelijk genormaliseerd
  // (blank-lines stripped, items hergeordend) — overschrijven van de
  // doc tijdens een edit-sessie laat de cursor springen en regels
  // verschuiven. Dirty-flag wordt gereset op blur en bij remount; daarna
  // mag de eerstvolgende text-prop-mutatie weer landen.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (dirtyRef.current) return;
    const current = view.state.doc.toString();
    if (current === text) return;
    view.dispatch({
      changes: { from: 0, to: current.length, insert: text },
      annotations: SyncSource.of("ae.sync"),
    });
  }, [text]);

  // Push the highlight-search into the editor whenever it changes (or
  // when the doc-content changes — the search is doc-relative, so we
  // re-evaluate it after the store-sync rewrites the buffer).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({ effects: setHighlightSearch.of(highlightSearch ?? null) });
  }, [highlightSearch, text]);

  // tabIndex={-1} + onFocus → forward naar CodeMirror's `cm-content`,
  // zodat Tab vanaf de header-knoppen direct in het tekst-gedeelte
  // landt. Zonder dit zou de wrapper-div geen Tab-stop hebben en
  // CodeMirror's eigen tabindex= -1 op de cm-editor onbereikbaar blijven.
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationeel focus-proxy-oppervlak voor de geneste CodeMirror-editor; geen semantische control
    <div
      ref={hostRef}
      className="h-full"
      role="presentation"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: wrapper is een focus-proxy voor de geneste CodeMirror-editor (cm-content heeft tabIndex=-1); deze div MOET een Tab-stop zijn om CodeMirror überhaupt bereikbaar te houden
      tabIndex={0}
      onFocus={(e) => {
        // Alleen forwarden wanneer focus precies op deze wrapper landt,
        // niet wanneer 't een bubble-event van een child (cm-content) is.
        if (e.target === hostRef.current) viewRef.current?.focus();
      }}
    />
  );
}

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised)"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // ignore
        }
      }}
    >
      {copied ? t("textPane.copy.copiedLower") : t("textPane.copy.labelLower")}
    </button>
  );
}
